import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { t } from 'i18next';
import './index.scss';
import Filter from '../filter';
import LangMenu from '../LangMenu';
import { Dropdown, Progress, Spin, Modal, message, Tooltip } from 'antd';
import { DownOutlined, LoadingOutlined } from '@ant-design/icons';
import { ChevronDownIcon } from 'tdesign-icons-react';
import { loadCaptcha } from 'utils/captcha';
import { getBroadcastTTS, GetBroadcastTTSReq, getBroadcastVideoProgress, collectTimbre } from 'apis';
import playIcon from './assets/play.svg';
import pauseIcon from './assets/pause.svg';
import men from './assets/men.svg';
import women from './assets/women.svg';
import star from './assets/star.svg';
import starActive from './assets/starActive.svg';
let timer: ReturnType<typeof setInterval> | null = null;
const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;
interface Iprops {
  timbre: any;
  value: any;
  onSelect: (value: any) => void;
  timbreAuditionTextList: {
    dictCode: string;
    dictValue: string;
  }[];
  tryout: any;
  tryoutValue: string;
  onCreateDemo: (value: string) => void;
  languageList?: any[];
  timbreFilter?: any;
  refreshList?: (timbreCode: string) => void;
  tabKey?: number;
}
interface Item {
  code: string;
  name: string;
  flag?: string;
}
const TimbreItem = (props: Iprops) => {
  const {
    timbre: {
      timbreGender,
      isRecommend,
      supportLanguages,
      labels,
      timbreTag,
      isCollected,
      emotionalStyles,
      timbreName,
      timbreCode,
      isInvalid,
      timbreCategory,
      timbreClassification,
      platformSource,
      timbreType,
      language,
      timbreUrl,
      thirdSource,
    },
    value: { emotionCategory, timbreCode: currentTimbreCode, timbreLanguage },
    onSelect,
    timbreAuditionTextList,
    tryoutValue,
    onCreateDemo,
    languageList,
    tryout: { play, pause, isPausing, isPlaying, isLoading, currentTime, duration, reset, value: tryValue, load },
    timbreFilter: { timbreSelectedLanguage },
    refreshList,
    tabKey,
  } = props;
  const curStyle = emotionalStyles?.find((item: { code: any }) => item.code === emotionCategory);
  const [timbreCategoryVal, setTimbreCategoryVal] = useState(
    timbreCode === currentTimbreCode ? curStyle || emotionalStyles?.[0] : emotionalStyles?.[0],
  );
  const curStyleRef = useRef<Item>(curStyle || emotionalStyles?.[0]);
  const curLanguage = supportLanguages?.find((item: { code: any }) => item.code === timbreLanguage);
  const defaultLang = supportLanguages?.length
    ? supportLanguages[0]
    : languageList?.find((item) => item.code === language);
  const [timbreLanguageVal, setTimbreLanguageVal] = useState(
    timbreCode === currentTimbreCode ? curLanguage || defaultLang : defaultLang,
  );
  const languageRef = useRef<Item>(timbreCode === currentTimbreCode ? curLanguage || defaultLang : defaultLang);
  const [hasChange, setHasChange] = useState(false);
  useEffect(() => {
    if (timbreSelectedLanguage) {
      const selectedLanguage = supportLanguages?.find((item: { code: any }) => item.code === timbreSelectedLanguage);
      if (selectedLanguage) {
        languageRef.current = selectedLanguage;
        setTimbreLanguageVal(selectedLanguage);
      }
      return;
    }
  }, [timbreSelectedLanguage]);
  useEffect(() => {
    if (emotionCategory && timbreCode === currentTimbreCode) {
      const selectedStyle = emotionalStyles?.find((item: { code: any }) => item.code === emotionCategory);
      if (selectedStyle) {
        curStyleRef.current = selectedStyle;
        setTimbreCategoryVal(selectedStyle);
      }
    }
  }, [emotionCategory]);
  const [isCollect, setIsCollect] = useState(isCollected);
  const [auditionLoading, setAuditionLoading] = useState(false);
  const [isTextOverflowing, setIsTextOverflowing] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  // 检测文本是否溢出
  const checkTextOverflow = useCallback(() => {
    if (textRef.current) {
      const element = textRef.current;
      const isOverflowing = element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;
      setIsTextOverflowing(isOverflowing);
    }
  }, []);

  // 监听文本变化和组件挂载后检测溢出
  useEffect(() => {
    checkTextOverflow();
    // 添加 resize 监听，以防容器大小变化
    const handleResize = () => {
      checkTextOverflow();
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [timbreName, checkTextOverflow]);

  const onClickLanguage = (value: Item) => {
    languageRef.current = value;
    setTimbreLanguageVal(value);
    setHasChange(true);
  };
  useEffect(() => {
    if (timbreLanguage && timbreCode === currentTimbreCode) {
      const selectedLanguage = supportLanguages?.find((item: { code: any }) => item.code === timbreLanguage);
      if (selectedLanguage && selectedLanguage.code !== timbreCategoryVal?.code) {
        languageRef.current = selectedLanguage;
        setTimbreLanguageVal(selectedLanguage);
      }
      return;
    }
  }, []);
  const langMenu =
    supportLanguages?.length > 0 ? (
      <LangMenu
        timbreLanguages={supportLanguages as Item[]}
        onClick={onClickLanguage}
        curLanguage={timbreLanguageVal}
      />
    ) : (
      <></>
    );
  const LangsBtnContent = () => {
    return (
      <div className="language-filter">
        <span className="language-filter-name">{timbreLanguageVal?.name || ''}</span>
        {supportLanguages?.length > 0 && <ChevronDownIcon className="language-filter-icon" />}
      </div>
    );
  };
  const status = useMemo(() => {
    if (tryoutValue === timbreCode && auditionLoading) {
      return 'loading';
    }
    if (tryValue === timbreCode) {
      if (isLoading) {
        return 'loading';
      }
      if (isPausing) {
        return 'playing';
      }
      if (isPlaying) {
        return 'paused';
      }
    }
    return 'none';
  }, [isLoading, isPausing, isPlaying, timbreCode, tryValue, auditionLoading, tryoutValue]);
  type sideType = 'gender' | 'age' | 'timbreLanguage' | 'timbreStyle';
  useEffect(() => {
    if (tryoutValue !== timbreCode) {
      if (timer) {
        clearInterval(timer);
      }
      if (isPlaying) {
        reset();
      }
      setAuditionLoading(false);
    }
  }, [tryoutValue]);
  const renderStyle = (timbreStyle: Item[]) => {
    const sideTabData = {
      type: 'timbreStyle' as sideType,
      value: timbreStyle.map((item) => ({
        ...item,
        value: item.code,
      })),
      defaultcode: timbreCategoryVal?.name || curStyleRef.current?.name,
    };
    const handleSideVal = (data: any) => {
      const cur = emotionalStyles.find((i: { code: any }) => i.code == data);
      curStyleRef.current = cur || { code: '0', name: '' };
      setTimbreCategoryVal(cur);
      setHasChange(true);
      itemClick();
    };
    return (
      <Filter
        simple={true}
        showLabel={true}
        sideTabData={sideTabData}
        handleSideVal={(data) => handleSideVal(data)}
        className="style-filter"
      />
    );
  };
  const itemClick = async () => {
    const params = {
      timbreCode,
      timbreLanguage: languageRef.current?.code,
      emotionCategory: curStyleRef.current?.code,
      timbreDetail: {
        ...props.timbre,
      },
    };
    onSelect?.(params);
  };
  const reqAudition = () => {
    const text =
      timbreAuditionTextList?.find((i) => i.dictCode === languageRef.current?.code)?.dictValue ||
      timbreAuditionTextList?.find((item) => item.dictCode === 'eng')?.dictValue ||
      '';
    const payload = {
      timbreKey: timbreCode,
      speed: 1,
      volume: 0,
      inputSsmlArray: [text],
      platformSource,
      timbreCategory,
      timbreLanguage: languageRef.current?.code,
      emotionCategory: curStyleRef.current?.code,
      isAudition: true,
    };
    getTTSAuditionId({
      ...payload,
    });
  };

  const getCaptcha = (payload: GetBroadcastTTSReq) => {
    const callback = (res: {
      ret: number;
      ticket: string;
      randstr: string;
      errorCode?: number;
      errorMessage?: string;
    }) => {
      const { ret, errorCode } = res;
      // 只有验证成功时才继续调用
      if (ret === 0) {
        getTTSAuditionId(payload, { ticket: res.ticket, randomData: res.randstr });
        return;
      }

      // 用户主动关闭验证码或者失败不再重试
      if ([2].includes(ret)) {
        message.info(t('验证码关闭'));
        return;
      }

      if (errorCode === 1001) {
        message.warning(t('加载验证码服务失败，请刷新重试'));
        return;
      }
    };
    loadCaptcha(callback);
  };
  const getTTSAuditionId = async (
    payload: GetBroadcastTTSReq,
    capchaParams?: { ticket: string; randomData: string },
  ) => {
    setAuditionLoading(true);

    try {
      const taskIds = await getBroadcastTTS({ ...payload, ...capchaParams, tTSMode: 1 });
      const { timbreKey } = payload;
      if (!taskIds?.length) {
        setAuditionLoading(false);
        return;
      }
      timer = setInterval(() => {
        getTTSAuditionResult(taskIds[0], timbreKey);
      }, 1500);
      return;
    } catch (err: any) {
      setAuditionLoading(false);
      if (err?.code === 'MissingParameter') {
        getCaptcha(payload);
      } else {
        message.error(err.message || t('请求失败'));
      }
    }
  };

  const getTTSAuditionResult = (name: string, key: string) => {
    getBroadcastVideoProgress({ name })
      .then(({ videoUrl, status }) => {
        if (status === 1) {
          if (timer) clearInterval(timer);
          setAuditionLoading(false);
          load(videoUrl, timbreCode);
        }
      })
      .catch(() => {
        stop();
        if (timer) clearInterval(timer);
        setAuditionLoading(false);
      });
  };
  const onCollect = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    collectTimbre({
      timbreKey: timbreCode,
      timbreClassification: String(timbreClassification),
    }).then((res) => {
      setIsCollect(res.collectStatus);
      if (!res.collectStatus && tabKey === 4) {
        refreshList?.(timbreCode);
      }
    });
  };
  const onAudioClick = (e: { stopPropagation: () => void }) => {
    if (status === 'loading') return;
    if (timbreCode === tryValue && !hasChange) {
      if (isPausing) {
        play();
        return;
      }
      if (isPlaying) {
        pause();
        return;
      }
    } else {
      onCreateDemo(timbreCode);
      if (timbreClassification === 3 && timbreUrl) {
        load(timbreUrl, timbreCode);
        return;
      }
      reqAudition();
      setHasChange(false);
    }
  };
  return (
    <>
      <div
        className={`timbre-item ${currentTimbreCode === timbreCode ? 'timbre-item-active' : ''}`}
        onClick={itemClick}
      >
        {isRecommend === 1 ? <div className="timbre-item-top-left-commend">{t('推荐')}</div> : null}
        {/* {isInvalid ? <div className="timbre-item-top-left-isInvalid">{t('已失效')}</div> : null} */}
        <div className={`timbre-item-top-right ${isCollect ? 'show-collect' : ''}`} onClick={onCollect}>
          <img src={isCollect ? starActive : star} />
        </div>
        <div
          className="timbre-item-left"
          onClick={(e) => {
            if (timbreClassification === 3 && !timbreUrl) return;
            onAudioClick(e);
          }}
        >
          {status === 'paused' && currentTime < duration && tryValue === timbreCode && (
            <Progress
              type="circle"
              percent={(currentTime / duration) * 100}
              width={48}
              showInfo={false}
              className="audio-play-progress"
              strokeColor="rgba(0, 82, 217, 1)"
              strokeWidth={4}
              trailColor="#DBE8FF"
            />
          )}
          <div className={`audio-play-button ${timbreClassification === 3 && !timbreUrl ? 'play-disabled' : ''}`}>
            {status === 'loading' && <Spin indicator={antIcon} />}
            {(status === 'playing' || status === 'none') && <img src={playIcon} />}
            {status === 'paused' && <img src={pauseIcon} />}
          </div>
        </div>
        <div className="timbre-item-right">
          <div className="top">
            <Tooltip title={isTextOverflowing ? timbreName : ''} placement="top">
              <div ref={textRef} className={`top-name`}>
                {timbreName}
              </div>
            </Tooltip>
            {timbreClassification !== 3 && timbreLanguageVal?.name && (
              <div className="top-language">
                {supportLanguages?.length > 1 ? (
                  <Dropdown
                    trigger={['click']}
                    overlay={langMenu}
                    overlayStyle={{
                      backgroundColor: '#fff',
                      padding: 6,
                      borderRadius: 6,
                      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    {LangsBtnContent()}
                  </Dropdown>
                ) : (
                  LangsBtnContent()
                )}
              </div>
            )}
            {emotionalStyles.length ? <div className="top-style">{renderStyle(emotionalStyles)}</div> : null}
          </div>
          <div className="bottom">
            <div className="bottom-tags">{timbreGender === 1 ? t('男性') : t('女性')}</div>
            {timbreTag &&
              timbreTag?.split(';')?.map((item: any) => (
                <div className="bottom-tags" key={item}>
                  {item}
                </div>
              ))}
            {timbreClassification === 2 && (
              <div className="bottom-tags">{labels?.includes('VOICE') ? t('基础版') : t('极速版')}</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
export default TimbreItem;
